import { Accessor, Component, JSX, createContext, createSignal, onCleanup, onMount, useContext } from "solid-js";
import { A, useLocation, useNavigate } from "@solidjs/router";
import { EMPTY_AUTH, getAuthState, openPolarPortal, signOut } from "./auth";
import type { AuthState } from "./auth";

const AuthContext = createContext<Accessor<AuthState>>(() => EMPTY_AUTH);
export const useAuth = () => useContext(AuthContext);

const App: Component<{ children?: JSX.Element }> = (props) => {
  const [authState, setAuthState] = createSignal<AuthState>(EMPTY_AUTH);
  const [darkMode, setDarkMode] = createSignal(
    typeof document !== "undefined" && document.documentElement.classList.contains("theme-dark"),
  );
  const location = useLocation();
  const navigate = useNavigate();
  const refresh = async () => setAuthState(await getAuthState());

  onMount(() => {
    void refresh();
    window.addEventListener("rrp:auth", refresh);
  });
  onCleanup(() => window.removeEventListener("rrp:auth", refresh));

  const isLanding = () => location.pathname === "/";
  const handleSignOut = async () => {
    try {
      await signOut();
      setAuthState({ ...EMPTY_AUTH, loading: false });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Sign out failed", error);
      await refresh();
    }
  };
  const handleManagePlan = async () => {
    try {
      await openPolarPortal();
    } catch (error) {
      window.alert((error as Error).message);
    }
  };
  const toggleTheme = () => {
    const next = !darkMode();
    setDarkMode(next);
    document.documentElement.classList.toggle("theme-dark", next);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", next ? "#0d1512" : "#f4f0e8");
  };

  return (
    <AuthContext.Provider value={authState}>
      <div class={isLanding() ? "site-shell landing-shell" : "site-shell app-shell"}>
        <button
          class="theme-toggle"
          type="button"
          aria-label={darkMode() ? "Switch to light mode" : "Switch to dark mode"}
          aria-pressed={darkMode()}
          title={darkMode() ? "Light mode" : "Dark mode"}
          onClick={toggleTheme}
        >
          <span class="theme-icon theme-icon-sun" aria-hidden="true">☀︎</span>
          <span class="theme-icon theme-icon-moon" aria-hidden="true">☾</span>
        </button>
        <header class="topbar">
          <A href="/" class="brand" aria-label="Rating Races Pro home">
            <span class="brand-mark">R</span>
            <span>Rating Races <strong>Pro</strong></span>
          </A>
          <nav aria-label="Main navigation">
            {isLanding() ? (
              <>
                <a href="#method">Method</a>
                <a href="#features">Features</a>
                <a href="#pricing">Pricing</a>
              </>
            ) : (
              <>
                <A href="/app" activeClass="active">Races</A>
                <A href="/predictions" activeClass="active">Analysis</A>
                <A href="/pricing" activeClass="active">Pricing</A>
              </>
            )}
          </nav>
          <div class="nav-actions">
            {!authState().loading && authState().user_id ? (
              <>
                {!isLanding() && <span class="account-label">{authState().is_paid ? `${authState().plan === "annual" ? "Annual" : authState().plan === "monthly" ? "Monthly" : ""} Pro member` : "Free account"}</span>}
                {isLanding() && <A class="button button-quiet button-small" href="/app">Open dashboard</A>}
                {!isLanding() && authState().is_paid && <button class="button button-quiet button-small" onClick={handleManagePlan}>Manage plan</button>}
                {!isLanding() && <button class="button button-quiet button-small" onClick={handleSignOut}>Sign out</button>}
              </>
            ) : (
              <>
                <A class="nav-login" href="/login">Log in</A>
                <A class="button button-primary button-small" href="/login?mode=signup">Create account</A>
              </>
            )}
          </div>
        </header>
        <main class={isLanding() ? "landing-main" : "app-main"}>{props.children}</main>
        <aside class="site-disclaimer" aria-label="Important information">
          Rating Races Pro provides mathematical and statistical analysis only. It does not provide betting advice, gambling advice, sports picks or recommendations. Model outputs are informational, may be inaccurate, and do not guarantee results. You are solely responsible for your own decisions.
        </aside>
        <footer class="legal-footer" aria-label="Legal and contact information">
          <nav><A href="/privacy">Privacy</A><A href="/terms">Terms</A><A href="/cookies">Cookies</A><A href="/refunds">Cancellation & refunds</A></nav>
          <span>Dannan Kelly · Ireland · <a href="mailto:support@ratingraces.pro">support@ratingraces.pro</a> · <a href="tel:+353830674720">+353 83 067 4720</a></span>
        </footer>
      </div>
    </AuthContext.Provider>
  );
};

export default App;
